import { useMemo, useState } from "react";
import {
  Search,
  Layers,
  Package,
  Plus,
  X,
  FolderPlus,
  ListPlus,
} from "lucide-react";
import { useConfigStore } from "../store/configStore";
import { useEditorStore } from "../store/editorStore";
import { SkinThumbnail } from "./SkinThumbnail";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ParsedSkin, groupSkinsByCategory } from "../lib/spineParser";

export function SkinViewer() {
  const {
    parsedSkins,
    templateConfig,
  } = useConfigStore();
  const {
    setEditingSku,
    setPreviewSkinName,
    selectedSkins,
    toggleSkin,
    addSkuToSelection,
    removeSkuFromSelection,
    toggleRequireCategory,
  } = useEditorStore();
  const [search, setSearch] = useState("");

  // 获取 requireCategories
  const requireCategories: string[] = templateConfig?.requireCategories || [];

  // 按分类分组（使用预计算的 parsedSkins）
  const skinsByCategory = useMemo(() => {
    return groupSkinsByCategory(parsedSkins);
  }, [parsedSkins]);

  // 构建 skinName -> selectionInfo 的映射
  const skinToSelection = useMemo(() => {
    const map = new Map<
      string,
      { selectionId: string; selectionName: string; skuId: string }
    >();
    if (!templateConfig) return map;

    for (const selection of templateConfig.selection) {
      const skuIds = selection.skuIds || [];
      for (const skuId of skuIds) {
        const sku = templateConfig.skus?.[skuId];
        if (sku?.skinName) {
          map.set(sku.skinName, {
            selectionId: selection.id,
            selectionName: selection.name || selection.id,
            skuId: skuId,
          });
        }
      }
    }
    return map;
  }, [templateConfig]);

  // 过滤
  const filteredCategories = useMemo(() => {
    if (!search) return skinsByCategory;

    const searchLower = search.toLowerCase();
    const filtered = new Map<string, ParsedSkin[]>();

    for (const [category, categorySkins] of skinsByCategory) {
      const matchedSkins = categorySkins.filter(
        (skin) =>
          skin.name.toLowerCase().includes(searchLower) ||
          skin.category.toLowerCase().includes(searchLower)
      );
      if (matchedSkins.length > 0) {
        filtered.set(category, matchedSkins);
      }
    }
    return filtered;
  }, [skinsByCategory, search]);

  // 统计
  const totalSkins = parsedSkins.length;
  const addedSkinsCount = skinToSelection.size;

  // 换装预览 - 基于 Slot 互斥（使用 store 的 toggleSkin）
  const handleSkinClick = (skinName: string) => {
    // 调用 store 的 toggleSkin（内置 slot 互斥逻辑，可能被阻止）
    toggleSkin(skinName, "");

    // 始终显示详情面板（独立于 toggleSkin 的结果）
    const found = skinToSelection.get(skinName);
    if (found) {
      setEditingSku(found.skuId, found.selectionId);
    } else {
      setPreviewSkinName(skinName);
    }
  };

  // 添加到 selection
  const handleAddToSelection = (skinName: string, selectionId: string) => {
    addSkuToSelection(selectionId, skinName);
  };

  // 从 selection 移除
  const handleRemoveFromSelection = (skinName: string) => {
    const found = skinToSelection.get(skinName);
    if (found) {
      removeSkuFromSelection(found.selectionId, found.skuId);
      setEditingSku(null, null);
    }
  };

  if (parsedSkins.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
        暂无数据
      </div>
    );
  }

  const selections = templateConfig?.selection || [];

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* 说明 */}
      <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded-lg">
        <strong>Spine Skins</strong> - 每个 Skin
        是一个完整的配件（如一款发型），由多个 Slot/Attachment
        组成。点击预览，使用 <Plus className="h-3 w-3 inline" /> 添加到
        Selection。
      </div>

      {/* 搜索框 */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="搜索 Skin..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* 统计 */}
      <div className="text-xs text-muted-foreground flex items-center gap-2">
        <span>{totalSkins} 个 Skins</span>
        <span>•</span>
        <span>{filteredCategories.size} 个分类</span>
        <span>•</span>
        <span className="text-primary font-medium">
          {addedSkinsCount} 已添加
        </span>
        {requireCategories.length > 0 && (
          <>
            <span>•</span>
            <span className="text-amber-600 font-medium">
              {requireCategories.length} 必选
            </span>
          </>
        )}
      </div>

      {/* 无 Selection 提示 */}
      {selections.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          <strong>提示：</strong>请先在{" "}
          <Badge variant="outline" className="mx-1">
            Selection
          </Badge>{" "}
          页面创建 Selection，然后再添加 Skin。
        </div>
      )}

      {/* Skins 列表（按分类，可折叠，同时只展开一个） */}
      <ScrollArea className="flex-1">
        <Accordion
          type="single"
          collapsible
          defaultValue={Array.from(filteredCategories.keys())[0]}
          className="pr-4"
        >
          {Array.from(filteredCategories.entries()).map(
            ([category, categorySkins]) => {
              const addedCount = categorySkins.filter((s) =>
                skinToSelection.has(s.name)
              ).length;
              const isRequired = requireCategories.includes(category);

              return (
                <AccordionItem
                  key={category}
                  value={category}
                  className={cn(
                    "border-b-0",
                    isRequired && "bg-amber-50/50 rounded-lg"
                  )}
                >
                  {/* 分类标题 */}
                  <AccordionTrigger className="py-2 hover:no-underline">
                    <div className="flex items-center gap-2 flex-1">
                      {/* 必选勾选框 */}
                      <Checkbox
                        checked={isRequired}
                        onCheckedChange={() => toggleRequireCategory(category)}
                        onClick={(e) => e.stopPropagation()}
                        title="勾选后随机换装时必选此分类"
                        className="shrink-0"
                      />
                      <Layers className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">{category}</span>
                      <Badge
                        variant={
                          addedCount < categorySkins.length
                            ? "destructive"
                            : "secondary"
                        }
                        className={cn(
                          "text-[10px]",
                          addedCount === categorySkins.length &&
                            "bg-emerald-100 text-emerald-700 border-emerald-300"
                        )}
                      >
                        {addedCount}/{categorySkins.length}
                      </Badge>

                      {/* 批量添加 - 在箭头左边 */}
                      {selections.length > 0 &&
                        categorySkins.length > addedCount && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-[10px] gap-1 border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 ml-auto mr-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ListPlus className="h-3 w-3" />
                                全部添加
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-48 max-h-64 overflow-y-auto"
                            >
                              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground sticky top-0 bg-popover">
                                批量添加到
                              </div>
                              <DropdownMenuSeparator />
                              {selections.map((sel: any) => (
                                <DropdownMenuItem
                                  key={sel.id}
                                  onClick={() => {
                                    for (const skin of categorySkins) {
                                      if (!skinToSelection.has(skin.name)) {
                                        addSkuToSelection(sel.id, skin.name);
                                      }
                                    }
                                  }}
                                  className="text-xs"
                                >
                                  <FolderPlus className="h-3.5 w-3.5 mr-2" />
                                  {sel.name || sel.id}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                    </div>
                  </AccordionTrigger>

                  {/* Skin 网格 */}
                  <AccordionContent>
                    <div className="grid grid-cols-4 gap-2 pt-2">
                      {categorySkins.map((skin) => {
                        const isSelected = selectedSkins.includes(skin.name);
                        const selectionInfo = skinToSelection.get(skin.name);
                        const isAdded = !!selectionInfo;

                        return (
                          <div
                            key={skin.name}
                            className={cn(
                              "relative rounded-lg border p-1.5 transition-colors",
                              isSelected
                                ? "bg-primary/10 border-primary/50 ring-2 ring-primary/30"
                                : isAdded
                                  ? "bg-emerald-50 border-emerald-200"
                                  : "bg-muted/30 hover:bg-muted/50"
                            )}
                          >
                            {/* 缩略图 */}
                            <div
                              className="flex justify-center mb-1 cursor-pointer"
                              onClick={() => handleSkinClick(skin.name)}
                            >
                              <SkinThumbnail skinName={skin.name} size={48} />
                            </div>

                            {/* Skin Name + Category + Attachment Count */}
                            <div className="text-center mb-1">
                              <span
                                className="truncate block text-[10px] font-mono text-muted-foreground"
                                title={skin.name}
                              >
                                {skin.name.split("/").pop()}
                              </span>
                              <div className="flex items-center justify-center gap-1">
                                <span
                                  className="truncate text-[9px] text-primary/70"
                                  title={skin.category}
                                >
                                  {skin.category}
                                </span>
                                <Badge
                                  variant="secondary"
                                  className="text-[8px] px-1 h-3.5"
                                >
                                  {skin.attachmentCount}
                                </Badge>
                              </div>
                            </div>

                            {/* 添加/移除按钮 */}
                            <div className="flex justify-center">
                              {isAdded ? (
                                <div className="flex items-center gap-1">
                                  <Badge
                                    variant="outline"
                                    className="text-[9px] px-1 h-5 bg-emerald-50 border-emerald-300 text-emerald-700 truncate max-w-[60px]"
                                    title={selectionInfo.selectionName}
                                  >
                                    {selectionInfo.selectionName}
                                  </Badge>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveFromSelection(skin.name);
                                    }}
                                    title="从 Selection 移除"
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : selections.length > 0 ? (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-5 px-1.5 text-[10px] gap-0.5"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Plus className="h-3 w-3" />
                                      添加
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    align="center"
                                    className="w-48 max-h-64 overflow-y-auto"
                                  >
                                    <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground sticky top-0 bg-popover">
                                      添加到 Selection
                                    </div>
                                    <DropdownMenuSeparator />
                                    {selections.map((sel: any) => (
                                      <DropdownMenuItem
                                        key={sel.id}
                                        onClick={() =>
                                          handleAddToSelection(
                                            skin.name,
                                            sel.id
                                          )
                                        }
                                        className="text-xs"
                                      >
                                        <FolderPlus className="h-3.5 w-3.5 mr-2" />
                                        {sel.name || sel.id}
                                        <Badge
                                          variant="secondary"
                                          className="ml-auto text-[10px]"
                                        >
                                          {(sel.skuIds || []).length}
                                        </Badge>
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className="text-[9px] px-1 h-5 text-muted-foreground"
                                >
                                  无 Selection
                                </Badge>
                              )}
                            </div>

                            {/* 选中指示器 */}
                            {isSelected && (
                              <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                                <span className="text-[8px] text-primary-foreground">
                                  ✓
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            }
          )}
        </Accordion>
      </ScrollArea>
    </div>
  );
}
